import React from 'react';
import { Card, Descriptions, Spin, Empty, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getVendorById } from '@/api/vendors';
import { useAuth } from '@/contexts/AuthContext';


interface VendorTabProps {
  vendorId: string;
}

const VendorTab: React.FC<VendorTabProps> = ({ vendorId }) => {
  const { t } = useTranslation(['vendors', 'common']);
  const { firestoreUser } = useAuth();

  const { data: vendor, isLoading, error } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => getVendorById(vendorId, firestoreUser!),
    enabled: !!vendorId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !vendor) {
    return (
      <Card>
        <Empty description={t('vendors:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('vendors:fields.name')}>
          {vendor.name}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.type')}>
          <Tag color="blue">{t(`vendors:types.${vendor.type}`)}</Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.status')}>
          <Tag color={vendor.status === 'active' ? 'green' : 'red'}>
            {t(`vendors:status.${vendor.status}`)}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.contactPerson')}>
          {vendor.contactPerson}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.category')}>
          {vendor.category}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.email')}>
          {vendor.email || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.phone')}>
          {vendor.phone}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.address')}>
          {vendor.address}
        </Descriptions.Item>
        
        <Descriptions.Item label="Services">
          {vendor.services?.length ? (
            <div>
              {vendor.services.map((service, index) => (
                <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                  {service}
                </Tag>
              ))}
            </div>
          ) : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('vendors:fields.notes')}>
          {vendor.notes || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default VendorTab; 